/**
 * Sistema de Acordeones para IberoHemp CBD
 * Versión optimizada para Shopify con mejoras móviles
 */

class AcordeonesManager {
    constructor() {
        this.acordeones = [];
        this.activeAcordeon = null;
        this.isMobile = window.innerWidth <= 768;
        this.touchStartY = 0;
        this.init();
    }
    
    init() {
        this.setupEventListeners();
        this.setupMobileOptimizations();
        console.log('✅ Acordeones inicializados');
    }
    
    setupEventListeners() {
        const acordeonTriggers = document.querySelectorAll('.acordeon-trigger');
        
        acordeonTriggers.forEach((trigger, index) => {
            const acordeonItem = trigger.closest('.acordeon-item');
            const content = acordeonItem.querySelector('.acordeon-content');
            
            this.acordeones.push({
                trigger,
                content,
                item: acordeonItem,
                index
            });
            
            // Evento click principal
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleAcordeon(index);
            });
            
            // Optimizaciones para móvil
            if (this.isMobile) {
                this.setupMobileEvents(trigger, index);
            }
        });
    }
    
    setupMobileEvents(trigger, index) {
        // Evitar añadir listeners duplicados
        if (trigger.dataset.mobileBound === 'true') return;
        trigger.dataset.mobileBound = 'true';

        // Prevenir zoom en doble tap
        let lastTap = 0;
        
        trigger.addEventListener('touchend', (e) => {
            const currentTime = new Date().getTime();
            const tapLength = currentTime - lastTap;
            
            if (tapLength < 500 && tapLength > 0) {
                e.preventDefault();
                this.toggleAcordeon(index);
            }
            lastTap = currentTime;
        });
        
        // Mejorar feedback táctil
        trigger.addEventListener('touchstart', (e) => {
            trigger.style.transform = 'scale(0.98)';
        });
        
        trigger.addEventListener('touchend', (e) => {
            setTimeout(() => {
                trigger.style.transform = '';
            }, 150);
        });
        
        // Prevenir scroll accidental
        trigger.addEventListener('touchmove', (e) => {
            e.preventDefault();
        }, { passive: false });
    }
    
    setupMobileOptimizations() {
        // Detectar cambios de orientación
        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                this.isMobile = window.innerWidth <= 768;
                this.updateMobileState();
                this.adjustSectionHeight();
            }, 100);
        });
        
        // Detectar cambios de tamaño de ventana
        window.addEventListener('resize', () => {
            const wasMobile = this.isMobile;
            this.isMobile = window.innerWidth <= 768;
            
            if (wasMobile !== this.isMobile) {
                this.updateMobileState();
            }
            
            // Ajustar altura cuando cambie el tamaño de la ventana
            this.adjustSectionHeight();
        });
    }
    
    updateMobileState() {
        // Ajustar comportamiento según el dispositivo
        this.acordeones.forEach((acordeon, index) => {
            if (this.isMobile) {
                this.setupMobileEvents(acordeon.trigger, index);
            }
        });
    }
    
    toggleAcordeon(index) {
        const acordeon = this.acordeones[index];
        if (!acordeon) return;
        
        const isExpanded = acordeon.trigger.getAttribute('aria-expanded') === 'true';
        
        if (isExpanded) {
            this.closeAcordeon(index);
        } else {
            this.openAcordeon(index);
        }
    }
    
    openAcordeon(index) {
        const acordeon = this.acordeones[index];
        if (!acordeon) return;
        
        // Cerrar acordeón activo si existe
        if (this.activeAcordeon !== null && this.activeAcordeon !== index) {
            this.closeAcordeon(this.activeAcordeon);
        }
        
        // Abrir el acordeón seleccionado
        acordeon.trigger.setAttribute('aria-expanded', 'true');
        acordeon.content.classList.add('active');
        acordeon.item.classList.add('active');
        
        this.activeAcordeon = index;
        
        // Asegurar que la sección se ajuste correctamente
        this.adjustSectionHeight();
        
        // Scroll suave en móvil
        if (this.isMobile) {
            this.scrollToAcordeon(acordeon.item);
        }
    }
    
    closeAcordeon(index) {
        const acordeon = this.acordeones[index];
        if (!acordeon) return;
        
        acordeon.trigger.setAttribute('aria-expanded', 'false');
        acordeon.content.classList.remove('active');
        acordeon.item.classList.remove('active');
        
        if (this.activeAcordeon === index) {
            this.activeAcordeon = null;
        }
        
        // Verificar si todos los acordeones están cerrados y ajustar la sección
        this.checkAllClosed();
        
        // Ajustar altura después de la transición
        setTimeout(() => {
            this.adjustSectionHeight();
        }, 500); // Tiempo de transición del CSS
    }
    
    scrollToAcordeon(acordeonItem) {
        // Scroll suave optimizado para móvil
        const rect = acordeonItem.getBoundingClientRect();
        const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
        
        if (!isInViewport) {
            const offset = 20; // Margen superior
            const targetPosition = acordeonItem.offsetTop - offset;
            
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        }
    }
    
    checkAllClosed() {
        // Verificar si todos los acordeones están cerrados
        const allClosed = this.acordeones.every(acordeon => 
            acordeon.trigger.getAttribute('aria-expanded') === 'false'
        );
        
        if (allClosed) {
            // Si todos están cerrados, ajustar la altura de la sección
            this.adjustSectionHeight();
        }
    }
    
    adjustSectionHeight() {
        // Obtener la sección de acordeones
        const section = document.querySelector('.acordeones-section');
        if (!section) return;
        
        // Forzar un reflow para asegurar que las alturas se calculen correctamente
        section.style.height = 'auto';
        
        // Usar requestAnimationFrame para asegurar que el cambio se aplique después del reflow
        requestAnimationFrame(() => {
            // Calcular la altura real del contenido
            const container = section.querySelector('.acordeones-container');
            if (container) {
                const containerHeight = container.offsetHeight;
                const containerTop = container.offsetTop;
                const totalHeight = containerTop + containerHeight;
                
                // Ajustar la altura mínima de la sección si es necesario
                if (totalHeight < window.innerHeight) {
                    section.style.minHeight = 'auto';
                } else {
                    section.style.minHeight = '100vh';
                }
            }
        });
    }
}

// Inicializar cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
    const manager = new AcordeonesManager();
    // Abrir el primer acordeón por defecto y ajustar altura inicial
    setTimeout(() => {
        manager.openAcordeon(0);
        manager.adjustSectionHeight();
    }, 0);
});